import React, {useMemo} from 'react';
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import InputAdornment from "@mui/material/InputAdornment";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";

const MONTH_NAMES = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];

const formatMonth = (isoMonth) => {
  const d = new Date(isoMonth);
  return `${MONTH_NAMES[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

/**
 * Settings panel for cashflow calculator
 * Allows user to configure starting capital, monthly expenses, and date range filter
 */
const CashflowSettings = ({settings, onSettingsChange, dateRange, onDateRangeChange, availableMonths}) => {
  const monthOptions = useMemo(() =>
    availableMonths.map(m => {
      // m is ISO timestamp like "2024-01-01T00:00:00.000Z", extract YYYY-MM
      const value = m.slice(0, 7);
      return {value, label: formatMonth(m)};
    }),
    [availableMonths]
  );

  const handleChange = (field) => (event) => {
    const value = parseFloat(event.target.value) || 0;
    onSettingsChange({
      ...settings,
      [field]: value
    });
  };

  const handleDateChange = (field) => (event) => {
    onDateRangeChange({
      ...dateRange,
      [field]: event.target.value
    });
  };

  return (
    <Card sx={{mb: 3, p: 2}}>
      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
        CASHFLOW SETTINGS
      </Typography>
      <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
        <TextField
          label="Starting Capital"
          type="number"
          size="small"
          value={settings.startingCapital || ''}
          onChange={handleChange('startingCapital')}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
          sx={{width: 180}}
        />
        <TextField
          label="Monthly Expenses"
          type="number"
          size="small"
          value={settings.monthlyExpenses || ''}
          onChange={handleChange('monthlyExpenses')}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
          }}
          sx={{width: 180}}
        />
        <Divider orientation="vertical" flexItem />
        <TextField
          select
          label="From Month"
          size="small"
          value={dateRange.startMonth}
          onChange={handleDateChange('startMonth')}
          sx={{width: 180}}
        >
          <MenuItem value="">
            <em>All</em>
          </MenuItem>
          {monthOptions.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </TextField>
        <TextField
          select
          label="To Month"
          size="small"
          value={dateRange.endMonth}
          onChange={handleDateChange('endMonth')}
          sx={{width: 180}}
        >
          <MenuItem value="">
            <em>All</em>
          </MenuItem>
          {monthOptions.map(opt => (
            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
          ))}
        </TextField>
      </Box>
    </Card>
  );
};

export default CashflowSettings;